// Single source of truth for the set of locales the site ships.
//
// Adding a locale = add ONE row here, then provide its content
// (`src/messages/<code>.json`, `content/blog/<code>/`, a privacy-policy entry,
// a distributor contact block) and regenerate the artifacts:
//   bun run i18n:generate && bun run search:generate && bun run locales:sync
//
// This module is intentionally dependency-free (no DOM, no app imports) so the
// Node/Bun generator scripts under `scripts/` can import it directly.

export type LocaleDefinition = {
  /** Short code used throughout the app and as the message-catalog filename. */
  code: string;
  /** Audience/market this locale serves; keys the distributor contact config. */
  market: string;
  /** Public URL prefix. Empty string = unprefixed (only the base locale). */
  pathPrefix: string;
  /** Extra incoming prefixes that also resolve to this locale (legacy/alias paths). */
  aliases: string[];
  /** Native language name, shown in the language switcher. */
  label: string;
  /** English language name, used in docs (llms.txt). */
  englishName: string;
  /** Human-readable market name, used in docs (llms.txt). */
  marketName: string;
  /** Open Graph og:locale value. */
  ogLocale: string;
  /** BCP-47 tag for Intl date/number formatting. */
  intl: string;
};

export const SITE_ORIGIN = "https://inext.ltd";

// The FIRST entry is the base locale: its catalog is the complete key set that
// every other locale overrides, and it is served unprefixed.
export const LOCALES = [
  {
    code: "en",
    market: "global",
    pathPrefix: "",
    aliases: ["/en"],
    label: "English",
    englishName: "English",
    marketName: "Global",
    ogLocale: "en_US",
    intl: "en-US",
  },
  {
    code: "uk",
    market: "ukraine",
    pathPrefix: "/ua",
    aliases: ["/uk"],
    label: "Українська",
    englishName: "Ukrainian",
    marketName: "Ukraine",
    ogLocale: "uk_UA",
    intl: "uk-UA",
  },
  {
    code: "pl",
    market: "poland",
    pathPrefix: "/pl",
    aliases: [],
    label: "Polski",
    englishName: "Polish",
    marketName: "Poland",
    ogLocale: "pl_PL",
    intl: "pl-PL",
  },
  {
    code: "pt",
    market: "brazil",
    pathPrefix: "/br",
    aliases: [],
    label: "Português",
    englishName: "Portuguese",
    marketName: "Brazil",
    ogLocale: "pt_BR",
    intl: "pt-BR",
  },
  {
    code: "es",
    market: "latam",
    pathPrefix: "/es",
    aliases: [],
    label: "Español",
    englishName: "Spanish",
    marketName: "Latin America",
    ogLocale: "es_LA",
    intl: "es-419",
  },
] as const satisfies readonly LocaleDefinition[];

export type Locale = (typeof LOCALES)[number]["code"];
export type Market = (typeof LOCALES)[number]["market"];

/** The unprefixed base locale whose catalog defines the full message key set. */
export const BASE_LOCALE: Locale = LOCALES[0].code;

/** Non-empty tuple of locale codes (typed for `z.enum`). */
export const locales = LOCALES.map((definition) => definition.code) as [Locale, ...Locale[]];

/** Non-empty tuple of distinct markets (typed for `z.enum`). */
export const markets = [...new Set(LOCALES.map((definition) => definition.market))] as [
  Market,
  ...Market[],
];

export function localeByCode(code: Locale): LocaleDefinition {
  const definition = LOCALES.find((entry) => entry.code === code);
  if (!definition) throw new Error(`Unknown locale: ${code}`);
  return definition;
}
